import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { getLocale } from "../translations";
import { generateQueryParams } from "../helpers/query";
import { authFetch } from "../../../global/auth-fetch.js";

const LOCAL_STORAGE_KEY = "klarnachrichten-filter-collections";
const ACTIVE_COLLECTION_KEY = "klarnachrichten-active-filter-collection";
const MAX_COLLECTIONS = 20;

function waitForAuth() {
  return new Promise((resolve) => {
    const store = window.klarnachrichten?.login?.app?.store;
    if (!store) { resolve(null); return; }

    const check = () => {
      const { isLoading, isAuthenticated, user } = store.getState().auth;
      if (!isLoading) {
        return isAuthenticated && user ? user : null;
      }
      return undefined; // still loading
    };

    const result = check();
    if (result !== undefined) { resolve(result); return; }

    const unsubscribe = store.subscribe(() => {
      const result = check();
      if (result !== undefined) {
        unsubscribe();
        resolve(result);
      }
    });
  });
}

export const useCollectionStore = defineStore("collections", () => {
  const collections = ref([]);
  const activeCollectionId = ref(null);

  async function init() {
    const user = await waitForAuth();
    let metadata = null;

    if (user?.id) {
      try {
        const res = await authFetch('/api/user/metadata');
        if (res.ok) {
          metadata = await res.json();
        }
      } catch (e) {
        console.error("Error loading user metadata:", e);
      }
    }

    await loadCollections(user, metadata);
    loadActiveCollectionId(user, metadata);
  }

  function createCollection(name, filters) {
    if (collections.value.length >= MAX_COLLECTIONS) {
      console.warn("Maximum number of filter collections reached");
      return null;
    }

    const id = Date.now().toString();

    collections.value.push({
      name,
      filters: {
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
        categories: Object.values(filters.categories)
          .filter((category) => category.active)
          .map((category) => {
            return {
              name: category.name,
              id: category.id,
            };
          }),
        locations: Object.values(filters.locations)
          .filter((location) => location.active)
          .map((location) => {
            return {
              name: location.name,
              id: location.id,
            };
          }),
        persons: Object.values(filters.persons)
          .filter((person) => person.active)
          .map((person) => {
            return {
              name: person.name,
              id: person.id,
            };
          }),
      },
      id: id,
    });

    saveCollections();

    return id;
  }

  function deleteCollection(id) {
    const index = collections.value.findIndex((collection) => collection.id === id);

    if (index !== -1) {
      collections.value.splice(index, 1);
    }

    saveCollections();
  }

  function renameCollection(id, name) {
    const index = collections.value.findIndex((collection) => collection.id === id);

    collections.value[index].name = name;

    saveCollections();
  }

  function serializeCollections() {
    const collectionsData = JSON.parse(JSON.stringify(collections.value));

    return collectionsData.map((collection) => {
      collection.filters.categories = collection.filters.categories.map((c) => c.id);
      collection.filters.persons = collection.filters.persons.map((p) => p.id);
      collection.filters.locations = collection.filters.locations.map((l) => l.id);
      return collection;
    });
  }

  function saveCollections() {
    const data = serializeCollections();
    const app = window.klarnachrichten?.login?.app;
    const store = app?.store;
    const auth = store?.getState()?.auth;

    if (auth?.isAuthenticated && auth?.user?.id) {
      // Save to Frontegg user metadata via backend API
      authFetch('/api/user/metadata', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fields: { filterCollections: JSON.stringify(data) },
        }),
      });
      // Clear localStorage after migration
      localStorage.removeItem(LOCAL_STORAGE_KEY);
    } else {
      // Fallback to localStorage for non-authenticated users
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(data));
    }
  }

  async function loadCollections(user, metadata) {
    let savedCollections = null;

    if (user?.id) {
      // Load from backend metadata
      if (metadata?.filterCollections) {
        try {
          savedCollections = JSON.parse(metadata.filterCollections);
        } catch (e) {
          console.error("Error parsing filter collections from metadata:", e);
        }
      }

      // Migration: If backend is empty but localStorage has data, migrate
      if (!savedCollections && localStorage.getItem(LOCAL_STORAGE_KEY)) {
        try {
          savedCollections = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY));
          if (savedCollections) {
            await authFetch('/api/user/metadata', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                fields: { filterCollections: JSON.stringify(savedCollections) },
              }),
            });
            localStorage.removeItem(LOCAL_STORAGE_KEY);
          }
        } catch (e) {
          console.error("Error migrating collections from localStorage:", e);
        }
      }
    } else {
      // Not authenticated — use localStorage
      if (localStorage.getItem(LOCAL_STORAGE_KEY)) {
        try {
          savedCollections = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY));
        } catch (e) {
          console.error("Error loading collections from localStorage:", e);
        }
      }
    }

    if (!savedCollections || savedCollections.length === 0) {
      return;
    }

    // Fetch names (depend on locale)
    const relationIds = {
      categories: new Set(),
      persons: new Set(),
      locations: new Set(),
    };

    savedCollections.forEach((collection) => {
      collection.filters.categories.forEach((c) => relationIds.categories.add(c));
      collection.filters.persons.forEach((p) => relationIds.persons.add(p));
      collection.filters.locations.forEach((l) => relationIds.locations.add(l));
    });

    const query = generateQueryParams({
      categories: [...relationIds.categories],
      persons: [...relationIds.persons],
      locations: [...relationIds.locations],
    });

    const response = await fetch(`/api/klarnachrichten/${getLocale()}/filter-options/names${query}`);
    const json = await response.json();

    if (!json.success) {
      console.log("Error: Unable to load collection filter names");
      return;
    }

    const names = json.data;

    collections.value = savedCollections.map((collection) => {
      collection.filters.categories = collection.filters.categories.map((c) => {
        return {
          id: c,
          name: names.categories[c],
        };
      });
      collection.filters.persons = collection.filters.persons.map((p) => {
        return {
          id: p,
          name: names.persons[p],
        };
      });
      collection.filters.locations = collection.filters.locations.map((l) => {
        return {
          id: l,
          name: names.locations[l],
        };
      });

      return collection;
    });
  }

  function loadActiveCollectionId(user, metadata) {
    if (user?.id) {
      if (metadata?.activeFilterCollectionId) {
        activeCollectionId.value = metadata.activeFilterCollectionId;
      }

      // Migration from localStorage
      if (!activeCollectionId.value && localStorage.getItem(ACTIVE_COLLECTION_KEY)) {
        activeCollectionId.value = localStorage.getItem(ACTIVE_COLLECTION_KEY);
        saveActiveCollectionId(activeCollectionId.value);
        localStorage.removeItem(ACTIVE_COLLECTION_KEY);
      }
    } else if (localStorage.getItem(ACTIVE_COLLECTION_KEY)) {
      activeCollectionId.value = localStorage.getItem(ACTIVE_COLLECTION_KEY);
    }
  }

  function saveActiveCollectionId(id) {
    const app = window.klarnachrichten?.login?.app;
    const store = app?.store;
    const auth = store?.getState()?.auth;

    if (auth?.isAuthenticated && auth?.user?.id) {
      authFetch('/api/user/metadata', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fields: { activeFilterCollectionId: id },
        }),
      });
    } else {
      if (id) {
        localStorage.setItem(ACTIVE_COLLECTION_KEY, id);
      } else {
        localStorage.removeItem(ACTIVE_COLLECTION_KEY);
      }
    }
  }

  function setActiveCollectionId(id) {
    activeCollectionId.value = id;
    saveActiveCollectionId(id);
  }

  function resetActiveCollection() {
    activeCollectionId.value = null;
    saveActiveCollectionId(null);
  }

  const hasAnyCollection = computed(() => {
    return collections.value.length > 0;
  });

  const activeCollection = computed(() => {
    const index = collections.value.findIndex((collection) => collection.id === activeCollectionId.value);

    if (index === -1) {
      return null;
    }

    return collections.value[index];
  });

  return {
    init,
    collections,
    createCollection,
    deleteCollection,
    renameCollection,
    hasAnyCollection,
    setActiveCollectionId,
    resetActiveCollection,
    activeCollection,
  };
});
